import React, { useEffect, useMemo, useState } from 'react';
import AuthLayout from '../../components/layout/AuthLayout';
import { useLazyGetOrganizationsQuery, useMassDeleteOrganizationsMutation } from '../../api/organizations.js';
import ThreeDotsLoader from '../../components/misc/ThreeDotsLoader.jsx';
import { Link } from 'react-router-dom';
import { getInitials } from '../../utils/getInitials.js';
import { PERMISSION_OWNED_ORGANIZATIONS, PERMISSION_PARTNER_ORGANIZATIONS } from '../../utils/constants.js';
import IconAdd from '../../components/images/IconAdd.jsx';
import { formatNumbers } from '../../utils/formatNumbers.js';
import { useErrorToast } from '../../hooks/useToast.jsx';
import { twMerge } from 'tailwind-merge';
import BaseButton from '../../components/form/BaseButton.jsx';
import IconDelete from '../../components/images/IconDelete.jsx';
import { debounce } from 'lodash';
import IconMultiSelect from '../../components/images/IconMultiSelect.jsx';
import useCurrentUser from '../../hooks/useCurrentUser.js';
import { useDebounce } from '../../hooks/useDebounce.js';
import BaseInput from '../../components/form/BaseInput.jsx';

const Organizations = () => {
    const { permissions: userPermissions } = useCurrentUser();
    const [isLoading, setIsLoading] = useState(false);
    const [selectMode, setSelectMode] = useState(false);
    const [refetch, setRefetch] = useState(false);
    const [confirmMassDelete, setConfirmMassDelete] = useState(false);
    const [organizationsResponse, setOrganizationResponse] = useState({});

    const [append, setAppend] = useState(false);

    const [selectedOrganizations, setSelectedOrganizations] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 500);
    const [page, setPage] = useState(1);
    const [hasMorePages, setHasMorePages] = useState(true);

    const resetOrganizationsState = () => {
        setPage(1);
        setAppend(false);
        setHasMorePages(true);
        setOrganizations([]);
    };

    const resetSearch = () => {
        setSearch('');
        resetOrganizationsState();
    };

    const searchByValue = (value) => {
        setSearch(value);
        setPage(1);
    };

    const changeSelectionMode = () => {
        setSelectMode(!selectMode);
        setSelectedOrganizations([]);
    };

    const requestParams = useMemo(() => {
        if (refetch) {
            setRefetch(false);
        }

        return {
            search: debouncedSearch,
            page,
            per_page: 10
        };
    }, [debouncedSearch, page, refetch]);

    const [getOrganizations] = useLazyGetOrganizationsQuery();

    const [massDeleteOrganizations, { isLoading: isLoadingMassDelete }] = useMassDeleteOrganizationsMutation();

    const submitDeleteOrganizations = async () => {
        try {
            await massDeleteOrganizations({ ids: selectedOrganizations }).then((apiResponse) => {
                if (apiResponse.data?.success) {
                    resetOrganizationsState();
                    setRefetch(true);
                } else {
                    useErrorToast(apiResponse.data?.message);
                }
            });
        } catch (error) {
            console.error('Failed to delete organizations:', error);
        }

        setSelectMode(false);
        setConfirmMassDelete(false);
        setSelectedOrganizations([]);
    };

    useEffect(() => {
        setIsLoading(true);

        (async () => {
            const result = await getOrganizations(requestParams);
            setOrganizationResponse(result.data);
        })();
    }, [getOrganizations, requestParams]);

    useEffect(() => {
        if (organizationsResponse?.response) {
            if (append) {
                setOrganizations((prevState) => {
                    return [...prevState, ...organizationsResponse.response.organizations];
                });
            } else {
                setOrganizations(organizationsResponse.response.organizations);
            }

            setHasMorePages(organizationsResponse.response.has_more_pages);
            setIsLoading(false);
            setOrganizationResponse({});
            setAppend(false);
        }
    }, [organizationsResponse, append]);

    useEffect(() => {
        let processing = false;

        const handleScroll = debounce(() => {
            if (
                Math.round(window.innerHeight + document.documentElement.scrollTop) + 10 >=
                document.documentElement.offsetHeight
            ) {
                if (hasMorePages && !isLoading && !processing) {
                    processing = true;
                    setIsLoading(true);
                    setPage((prevPage) => prevPage + 1);
                    setAppend(true);
                    setTimeout(() => {
                        processing = false;
                    }, 500);
                }
            }
        }, 300);

        window.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            window.removeEventListener('scroll', handleScroll);
            handleScroll.cancel();
        };
    }, [hasMorePages, isLoading]);

    return (
        <AuthLayout
            title='Organizations'
            backRoute='/organizations'
            forwardRoute='/organizations/add'
            forwardPermission={[PERMISSION_PARTNER_ORGANIZATIONS, PERMISSION_OWNED_ORGANIZATIONS]}
            forwardPermissionAttribute='create'
            forwardIcon={IconAdd}
            forwardIconClassName='fill-theme1-white'
        >
            <div className='max-w-3xl w-full mx-auto px-8 mt-6 mb-6'>
                <div className='flex justify-between mt-6 mb-4'>
                    <div className='flex-grow'>
                        <BaseInput
                            name='search'
                            type='text'
                            value={search}
                            placeholder='Search...'
                            setValue={(value) => searchByValue(value)}
                        />
                        <div className='mt-2 text-right'>
                            <Link
                                to='/organizations/advanced-search'
                                className='text-base text-theme1-title2 font-bold'
                            >
                                Advanced Search
                            </Link>
                        </div>
                    </div>
                    <div className='text-theme1-primary2 cursor-pointer ml-4 mt-2' onClick={() => resetSearch()}>
                        Reset
                    </div>
                </div>
                {(!!userPermissions[PERMISSION_OWNED_ORGANIZATIONS]?.delete ||
                    userPermissions[PERMISSION_PARTNER_ORGANIZATIONS]?.delete) && (
                    <div className='flex justify-between mt-4'>
                        <div onClick={() => changeSelectionMode()} className='cursor-pointer'>
                            <IconMultiSelect className='w-6 h-6 fill-theme1-dark' />
                        </div>
                    </div>
                )}
            </div>
            <div className='bg-theme1-tan text-white py-4 px-8 max-w-3xl w-full mx-auto mb-6'>
                <div className={`text-[22px] text-theme1-primary font-bold mb-4`}>Organizations List</div>
                <ul>
                    {organizations.map((org) => (
                        <li
                            key={org.id}
                            className='flex items-center justify-between py-4 border-t border-solid border-theme1-tanTransparent'
                        >
                            <div className='flex items-center'>
                                {selectMode && (
                                    <input
                                        id={'organization-' + org.id}
                                        type='checkbox'
                                        className={twMerge(
                                            'mr-2 h-6 w-6 appearance-none cursor-pointer rounded-full border border-theme1-primary',
                                            'checked:bg-theme1-tan relative',
                                            'before:absolute before:top-1/2 before:left-1/2 before:-translate-x-1/2 before:-translate-y-1/2',
                                            'before:content-[""] before:w-4 before:h-4 before:bg-no-repeat before:bg-center before:opacity-0',
                                            "checked:before:opacity-100 checked:before:bg-[url('/src/resources/images/checkmark.svg')]"
                                        )}
                                        value={org.id}
                                        onChange={(e) => {
                                            const { checked } = e.target;
                                            if (checked) {
                                                setSelectedOrganizations((prevState) => [...prevState, org.id]);
                                            } else {
                                                setSelectedOrganizations((prevState) =>
                                                    prevState.filter((id) => id !== org.id)
                                                );
                                            }
                                        }}
                                        checked={selectedOrganizations.includes(org.id)}
                                    />
                                )}
                                <div className='mr-4 bg-white flex items-center justify-center rounded-full w-9 h-9 text-theme1-primary'>
                                    {getInitials(org.name, 'organization')}
                                </div>

                                <div className='flex-grow'>
                                    {selectMode ? (
                                        <label
                                            htmlFor={'organization-' + org.id}
                                            className='text-base font-bold text-theme1-title2'
                                        >
                                            {org.name}
                                        </label>
                                    ) : (
                                        <Link
                                            to={`/organizations/${org.id}`}
                                            className='text-base font-bold text-theme1-title2'
                                        >
                                            {org.name}
                                        </Link>
                                    )}
                                    {org.industry && (
                                        <div className='text-xs text-theme1-title2 capitalize mt-1'>{org.industry}</div>
                                    )}
                                </div>
                            </div>

                            <div className='ml-auto text-xl font-bold text-theme1-title2 uppercase'>
                                {org.revenue !== null ? formatNumbers(org.revenue, '$') : '$0'}
                            </div>
                        </li>
                    ))}
                </ul>
                {isLoading && <ThreeDotsLoader />}
                {selectMode && (
                    <div className='z-20 fixed left-0 bottom-0 w-full bg-auth bg-center bg-cover rounded-t-4xl p-6'>
                        {confirmMassDelete ? (
                            <div>
                                <div className='text-white font-semibold text-lg text-center'>
                                    Are you sure you want to delete {selectedOrganizations.length} selected
                                    organizations?
                                </div>
                                <div className='flex justify-between items-center gap-4 mt-6'>
                                    <BaseButton
                                        className='btn-red-outline !text-theme1-white !border-theme1-white'
                                        textValue={'Cancel'}
                                        onClick={() => setConfirmMassDelete(false)}
                                    />
                                    <BaseButton
                                        className='btn-red-outline !text-theme1-white !border-theme1-white'
                                        textValue={'Delete'}
                                        onClick={() => submitDeleteOrganizations()}
                                    />
                                </div>
                            </div>
                        ) : (
                            <div className='flex justify-between items-center'>
                                <div className='cursor-pointer' onClick={() => setConfirmMassDelete(true)}>
                                    {selectedOrganizations.length > 0 && <IconDelete />}
                                </div>
                                <div className='text-white font-semibold text-lg'>
                                    {selectedOrganizations.length} Selected
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </AuthLayout>
    );
};

export default Organizations;
